import * as Sentry from '@sentry/react';
import type { Breadcrumb, ErrorEvent, Event } from '@sentry/react';

import type { RuntimeEnv } from '@/env';
import type { LogLevel } from '@/lib/logger';
import { scrubUrl } from '@/lib/scrubUrl';

export { scrubUrl } from '@/lib/scrubUrl';

/**
 * Sentry wiring for the frontend (proto-assessment P2 "No Observability":
 * "Sentry for frontend error tracking — wire to error boundaries").
 *
 * This is the ONLY module that imports the Sentry SDK. It plugs into the
 * existing PHI-safe seam:
 *   - `reportError()` (error boundary) → `captureAppError()` (creates issues).
 *   - `logger.error/warn/...`          → `addLogBreadcrumb()` (context only).
 *   - window.onerror / unhandledrejection → Sentry's default global handlers.
 *
 * PHI guardrails (this is a patient-data app — see CLAUDE.md "No PHI"):
 *   - `sendDefaultPii: false` — no IP / cookies / headers / request bodies.
 *   - `beforeSend` / `beforeSendTransaction` strip request bodies, cookies,
 *     headers, user identity, and mask ID-shaped URL path segments + drop query
 *     strings (which carry MRNs / names / person_ids).
 *   - `beforeBreadcrumb` drops raw `console` breadcrumbs and scrubs fetch / xhr
 *     / navigation URLs.
 *   - Session Replay is intentionally NOT enabled (would record patient screens).
 *
 * Sentry stays fully disabled unless `VITE_SENTRY_DSN` is set, so local / mock
 * dev is a no-op by default.
 */

let enabled = false;

/** PHI-free context attached to a captured exception (from `reportError`). */
export interface AppErrorContext {
  readonly source: string;
  readonly errorName?: string | undefined;
  readonly status?: number | undefined;
  readonly code?: string | undefined;
  readonly correlationId?: string | null | undefined;
  readonly componentStack?: string | undefined;
}

export function initSentry(env: RuntimeEnv): void {
  const { sentry } = env;
  if (sentry.dsn === undefined) return;

  const tracePropagationTargets = apiOrigin(env.apiBaseUrl);

  Sentry.init({
    dsn: sentry.dsn,
    environment: sentry.environment,
    enableLogs:true,
    release: sentry.release,
    // Never let the SDK attach IP, cookies, or auth headers.
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: sentry.tracesSampleRate,
    // Relay envelopes through a same-origin path when configured, so ad-blockers
    // / egress filters that drop `*.ingest.sentry.io` can't silently swallow
    // events. Needs a server relay at that path (dev: Vite proxy; prod: BFF).
    ...(sentry.tunnel ? { tunnel: sentry.tunnel } : {}),
    // When the API is a distinct origin, only propagate trace headers to it.
    // When relative / same-origin, omit the key so Sentry keeps its safe
    // same-origin default instead of disabling propagation.
    ...(tracePropagationTargets ? { tracePropagationTargets: [tracePropagationTargets] } : {}),
    // Browser noise that is never actionable.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
    ],
    beforeSend,
    // Inlined so `event` is inferred as the SDK's transaction-event type
    // (@sentry/react does not re-export that type to annotate against).
    beforeSendTransaction: (event) => {
      scrubRequest(event.request);
      delete event.user;
      if (typeof event.transaction === 'string') {
        event.transaction = scrubUrl(event.transaction);
      }
      return event;
    },
    beforeBreadcrumb,
  });

  enabled = true;
}

/**
 * Report an exception to Sentry with PHI-free context. Called from
 * `reportError()`; no-op when Sentry is disabled.
 */
export function captureAppError(error: unknown, context: AppErrorContext): void {
  if (!enabled) return;
  Sentry.withScope((scope) => {
    scope.setTag('source', context.source);
    scope.setLevel('error');
    scope.setContext('app_error', pruneUndefined({ ...context }));
    Sentry.captureException(error);
  });
}

/**
 * Record a structured-log event as a Sentry breadcrumb (context for the next
 * captured exception — it does NOT create an issue on its own). `meta` must be
 * already PHI-redacted by the caller (`logger.redact`). No-op when disabled.
 */
export function addLogBreadcrumb(
  level: LogLevel,
  event: string,
  meta?: Record<string, unknown>,
): void {
  if (!enabled) return;
  Sentry.addBreadcrumb({
    category: 'app.log',
    level: toSentryLevel(level),
    message: event,
    ...(meta ? { data: meta } : {}),
  });
}

// ---------------------------------------------------------------------------
// Scrubbing
// ---------------------------------------------------------------------------

function scrubRequest(request: Event['request']): void {
  if (!request) return;
  if (typeof request.url === 'string') request.url = scrubUrl(request.url);
  // These can carry session cookies, auth headers, query params, or bodies.
  delete request.query_string;
  delete request.cookies;
  delete request.headers;
  delete request.data;
}

function beforeSend(event: ErrorEvent): ErrorEvent | null {
  scrubRequest(event.request);
  // Never send user identity — could be an email / clinician PII.
  delete event.user;
  // Browser tracing stamps the error event's `transaction` with the active
  // route (e.g. `/patients/<guid>`), which carries a patient id. Mask it — the
  // request-url scrub above does not cover this field, and unlike
  // `beforeSendTransaction` this handler runs for error events.
  if (typeof event.transaction === 'string') {
    event.transaction = scrubUrl(event.transaction);
  }
  return event;
}

function beforeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  // Drop raw console output — it can echo arbitrary (unredacted) values.
  if (breadcrumb.category === 'console') return null;

  const data = breadcrumb.data;
  if (data) {
    if (
      (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') &&
      typeof data.url === 'string'
    ) {
      data.url = scrubUrl(data.url);
    }
    if (breadcrumb.category === 'navigation') {
      if (typeof data.from === 'string') data.from = scrubUrl(data.from);
      if (typeof data.to === 'string') data.to = scrubUrl(data.to);
    }
    // `app.log` breadcrumbs come from our logger seam (already redacted). Scrub
    // any URL/path-shaped fields as a last-line guard so a raw endpoint id can
    // never reach Sentry, whatever meta a caller passed to `logger.*`.
    if (breadcrumb.category === 'app.log') {
      if (typeof data.path === 'string') data.path = scrubUrl(data.path);
      if (typeof data.url === 'string') data.url = scrubUrl(data.url);
    }
  }
  return breadcrumb;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toSentryLevel(level: LogLevel): Sentry.SeverityLevel {
  return level === 'warn' ? 'warning' : level;
}

/** Origin of an absolute API base URL, or `null` for a relative one. */
function apiOrigin(apiBaseUrl: string): string | null {
  if (!/^https?:/i.test(apiBaseUrl)) return null;
  try {
    return new URL(apiBaseUrl).origin;
  } catch {
    return null;
  }
}

function pruneUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) out[key] = value;
  }
  return out;
}
